/* @jsx h */
import { TableStatus } from "@lemmonhq/types";
import { defineComponent, ref, h, computed } from "vue";
import { LemmonModal } from "../../components";
import { useAuth } from "../../providers";
import { qrcode } from "@lemmonhq/blogic/lib/receipt";
import { getTableJoinURL } from "@lemmonhq/orders";

export const TableQRModal = defineComponent<{
  tables: TableStatus[];
  tableId: number;
  onClose: Function;
}>({
  name: "TableQRModal",
  props: ["tables", "tableId", "onClose"],
  setup(props) {
    const auth = useAuth();
    const shopSlug = auth.user.value?.restaurant.slug;

    const table = props.tables.find(
      (el) => el.id === props.tableId,
    ) as TableStatus;
    const tableUrl = getTableJoinURL(
      import.meta.env.VITE_CUSTOMER_APP_PUBLIC_URL,
      shopSlug as string,
      table.table,
    );
    const qr = qrcode(6, "L");
    qr.addData(tableUrl);
    qr.make();

    // Create a larger QR code for downloads (8x size)
    const qrLarge = qrcode(6, "L");
    qrLarge.addData(tableUrl);
    qrLarge.make();

    const svgCode = ref(qr.createSvgTag());
    const dataUrlLarge = ref(qrLarge.createDataURL(15)); // 8x larger for downloads

    const currentTable = computed(() => {
      return props.tables.find((table) => table.id === props.tableId);
    });

    const downloadSvg = () => {
      const blob = new Blob([svgCode.value], { type: "image/svg+xml" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `table-${props.tableId}-qr.svg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    };

    const downloadGif = () => {
      const a = document.createElement("a");
      a.href = dataUrlLarge.value;
      a.download = `table-${props.tableId}-qr.gif`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    };

    const copyUrl = async () => {
      try {
        await navigator.clipboard.writeText(tableUrl);
        // Could add a toast notification here
      } catch (err) {
        console.error("Failed to copy URL:", err);
      }
    };

    return () => (
      <LemmonModal
        title={`${currentTable.value?.table.name} | QR Code`}
        onClose={props.onClose}
      >
        <div class="flex flex-col gap-6 p-6">
          {/* QR Code Display */}
          <div class="flex justify-center">
            <div class="bg-white p-6 rounded-2xl shadow-lg border border-gray-100">
              <div
                class="w-64 h-64 *:w-full *:h-full"
                v-html={svgCode.value ?? ""}
              />
            </div>
          </div>

          {/* Table Info */}
          <div class="text-center space-y-2">
            <h3 class="text-xl font-semibold text-accent">
              {currentTable.value?.table.name}
            </h3>
            <p class="text-sm font-mono bg-gray-50 px-3 py-2 rounded-lg break-all">
              {tableUrl}
            </p>
          </div>

          {/* Action Buttons */}
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <button class="btn btn-primary gap-2" onClick={copyUrl}>
              <span class="material-symbols-outlined text-lg">
                content_copy
              </span>
              Copy URL
            </button>

            <button class="btn btn-secondary gap-2" onClick={downloadSvg}>
              <span class="material-symbols-outlined text-lg">code</span>
              Download SVG
            </button>

            <button class="btn btn-neutral gap-2" onClick={downloadGif}>
              <span class="material-symbols-outlined text-lg">image</span>
              Download GIF
            </button>
          </div>

          {/* Info Card */}
          <div class=" border  rounded-xl p-4">
            <div class="flex items-center gap-3">
              <div class="flex justify-center items-center">
                <span class="material-symbols-outlined text-blue-600 text-6xl">
                  table_restaurant
                </span>
              </div>
              <div class="flex-1">
                <h4 class=" text-2xl text-accent font-semibold mb-1">
                  How to use this QR code
                </h4>
                <p class="text-sm ">
                  Customers can scan this QR code with their phone camera to
                  access your restaurant's menu and place orders directly from
                  this table.
                </p>
              </div>
            </div>
          </div>
        </div>
      </LemmonModal>
    );
  },
});
